// Focus lifecycle only: this does not add keyboard trapping or change the
// sheet's existing modal implementation.

/** @typedef {() => HTMLElement|null} FocusTarget */
/** @typedef {{panel:HTMLElement, targets:FocusTarget[], lastFocus:HTMLElement}} Entry */
/** @type {Entry[]} */
const openSheets = [];
/** @type {FocusTarget[]} */
let pendingReturn = [];
let closeEpoch = 0;

const topSheet = () => openSheets[openSheets.length - 1];

/** @param {HTMLElement|null} target */
const available = (target) => {
  if (!target?.isConnected || target === document.body || target.matches(":disabled")) return false;
  for (let node = /** @type {HTMLElement|null} */ (target); node; node = node.parentElement) {
    const style = window.getComputedStyle(node);
    if (
      node.hidden ||
      node.hasAttribute("inert") ||
      node.getAttribute("aria-hidden") === "true" ||
      style.display === "none" ||
      style.visibility === "hidden" ||
      style.visibility === "collapse"
    )
      return false;
  }
  return true;
};

/** @param {HTMLElement|null} target */
const focus = (target) => {
  if (!target || !available(target)) return false;
  target.focus({ preventScroll: true });
  return document.activeElement === target;
};

/** @param {HTMLElement} panel @param {FocusTarget} returnFocus */
export const registerSheetFocus = (panel, returnFocus) => {
  const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  // React runs child layout effects first when nested sheets mount together.
  // Register an ancestor beneath its already-registered descendants.
  const descendantIndex = openSheets.findIndex((sheet) => panel.contains(sheet.panel));
  const insertAt = descendantIndex < 0 ? openSheets.length : descendantIndex;
  const inherited =
    openSheets[insertAt]?.targets || openSheets[insertAt - 1]?.targets || pendingReturn;
  /** @type {Entry} */
  const entry = {
    panel,
    // Inherit the page opener through nested or replacement sheets. A removed
    // confirmation button must not erase the original destination.
    targets: [() => (panel.contains(trigger) ? null : trigger), returnFocus, ...inherited],
    lastFocus: panel,
  };
  for (const descendant of openSheets.slice(insertAt)) descendant.targets.push(returnFocus);
  openSheets.splice(insertAt, 0, entry);
  if (topSheet() === entry) focus(panel); // Never initially focus a purchase.

  const repair = () => {
    if (topSheet() !== entry) return;
    const active = document.activeElement;
    const previous = entry.lastFocus;
    // Recover controls lost to busy/catalog updates, not intentional outside focus.
    if ((active === document.body || active === previous) && !available(previous)) {
      focus(panel);
      entry.lastFocus = panel;
    }
  };
  // A child can update its own controls without rerendering Sheet (e.g. the
  // async pack catalog). Observe only this panel and disconnect on cleanup.
  const observer = new MutationObserver(repair);
  observer.observe(panel, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["disabled", "hidden", "style", "class", "aria-hidden", "inert"],
  });

  return {
    isTop: () => topSheet() === entry,
    /** @param {EventTarget|null} target */
    remember(target) {
      if (target instanceof HTMLElement && panel.contains(target)) entry.lastFocus = target;
    },
    repair,
    close() {
      observer.disconnect();
      const index = openSheets.indexOf(entry);
      if (index < 0) return;
      const wasTop = topSheet() === entry;
      openSheets.splice(index, 1);
      if (!wasTop) return;
      pendingReturn = entry.targets;
      const epoch = ++closeEpoch;
      // Wait for this React commit: a replacement sheet or destination page
      // may deliberately take focus before the old sheet's cleanup finishes.
      Promise.resolve().then(() => {
        if (epoch !== closeEpoch) return;
        pendingReturn = [];
        const active = document.activeElement;
        const remaining = topSheet();
        // A parent's mutation observer may already have repaired focus to its
        // neutral panel. Prefer the actual nested opener when still usable.
        if (remaining?.panel.contains(active) && active !== remaining.panel) return;
        if (
          active &&
          active !== document.body &&
          active !== remaining?.panel &&
          active.isConnected &&
          !panel.contains(active)
        )
          return;
        for (const resolve of entry.targets) {
          const target = resolve();
          if ((!remaining || remaining.panel.contains(target)) && focus(target)) return;
        }
        if (remaining) focus(remaining.panel);
      });
    },
  };
};
